import { setAverageInsightData, setUnopenedInsightData } from '@/lib/slices/insights-slice';
import { useAppStore } from '@/lib/hooks';
import CookieService from './general/cookie-service';

export interface InsightCookie {
  unopened: Record<string, { data: UnopenedInsights; count: number }>;
  average: Record<string, AverageInsights>;
}

const INSIGHT_COOKIE = 'insight-cache';
const DEFAULT_COOKIE: InsightCookie = { unopened: {}, average: {} };

const isValidInsightCookie = (cookie: any): cookie is InsightCookie => {
  return (
    cookie &&
    typeof cookie === 'object' &&
    'unopened' in cookie &&
    'average' in cookie &&
    typeof cookie.unopened === 'object' &&
    typeof cookie.average === 'object'
  );
};

const InsightsCacheService = {
  getInsightCookie: (): InsightCookie => {
    try {
      const cookie = CookieService.get(INSIGHT_COOKIE);
      // Cookie is already parsed by universal-cookie
      if (!cookie || typeof cookie !== 'object') {
        return DEFAULT_COOKIE;
      }
      // Validate cookie structure
      if (!isValidInsightCookie(cookie)) {
        return DEFAULT_COOKIE;
      }
      return cookie;
    } catch (error) {
      console.error('Error getting insight cookie:', error);
      return DEFAULT_COOKIE;
    }
  },

  updateInsightCookieUnopened: (
    groupId: string,
    courseId: string,
    data: UnopenedInsights,
    count: number,
  ): Record<string, { data: UnopenedInsights; count: number }> => {
    try {
      const current = InsightsCacheService.getInsightCookie();
      const key = `${groupId.substring(0, 14)},${courseId.substring(0, 14)}`;

      // Work on fresh, mutable copies so we don't mutate any frozen
      // objects that may have come from Redux state or previous cookies.
      const nextUnopened: Record<string, { data: UnopenedInsights; count: number }> = {
        ...current.unopened,
      };
      nextUnopened[key] = { data, count };

      const nextCookie: InsightCookie = {
        ...current,
        unopened: nextUnopened,
      };

      // universal-cookie will handle JSON stringification
      CookieService.set(INSIGHT_COOKIE, nextCookie);
      return nextUnopened;
    } catch (error) {
      console.error('Error updating unopened cookie:', error);
      return {};
    }
  },

  updateInsightCookieAverage: (
    groupId: string,
    data: AverageInsights,
  ): Record<string, AverageInsights> => {
    try {
      const current = InsightsCacheService.getInsightCookie();
      const key = `${groupId.substring(0, 14)}`;

      // Work on a fresh copy to avoid mutating potentially frozen objects.
      const nextAverage: Record<string, AverageInsights> = {
        ...current.average,
      };
      nextAverage[key] = data;

      const nextCookie: InsightCookie = {
        ...current,
        average: nextAverage,
      };

      // universal-cookie will handle JSON stringification
      CookieService.set(INSIGHT_COOKIE, nextCookie);
      return nextAverage;
    } catch (error) {
      console.error('Error updating average cookie:', error);
      return {};
    }
  },

  clearAverageCache: (groupId: string): void => {
    try {
      const current = InsightsCacheService.getInsightCookie();
      const key = `${groupId.substring(0, 14)}`;
      const nextAverage: Record<string, AverageInsights> = {
        ...current.average,
      };
      delete nextAverage[key];

      const nextCookie: InsightCookie = {
        ...current,
        average: nextAverage,
      };

      CookieService.set(INSIGHT_COOKIE, nextCookie);
    } catch (error) {
      console.error('Error clearing average cache:', error);
      CookieService.set(INSIGHT_COOKIE, DEFAULT_COOKIE);
    }
  },

  clearUnopenedCache: (groupId: string, courseId: string): void => {
    try {
      const current = InsightsCacheService.getInsightCookie();
      const key = `${groupId.substring(0, 14)},${courseId.substring(0, 14)}`;
      const nextUnopened: Record<string, { data: UnopenedInsights; count: number }> = {
        ...current.unopened,
      };
      delete nextUnopened[key];

      const nextCookie: InsightCookie = {
        ...current,
        unopened: nextUnopened,
      };

      CookieService.set(INSIGHT_COOKIE, nextCookie);
    } catch (error) {
      console.error('Error clearing unopened cache:', error);
      CookieService.set(INSIGHT_COOKIE, DEFAULT_COOKIE);
    }
  },

  resetInsightCookie: (): void => {
    try {
      CookieService.set(INSIGHT_COOKIE, DEFAULT_COOKIE);
    } catch (error) {
      console.error('Error resetting insight cookie:', error);
    }
  },
};

export default InsightsCacheService;
